import {memo, useCallback} from 'react';
import {createPortal} from 'react-dom';
import {useSelector} from 'react-redux';
import {getPydanticError} from '~/common/utils/errors';
import {useGuestApi} from '~/ViewInvitation/components/Guestlist/hooks/useGuestApi';
import {usePostsApi} from '~/ViewInvitation/components/Guestlist/hooks/usePostsApi';
import {
  selectIsPremium,
  selectIsPro,
  selectIsUpgrade,
} from '~/ViewInvitation/selectors/eventDetails';
import type {Guest} from '~/ViewInvitation/types/guests';
import {toastAlert} from '~library/atoms/Toast';
import {Typography} from '~library/atoms/Typography';
import {ResponsiveModal} from '~library/organisms/ResponsiveModal';
import {AlertLineIcon} from '~library/svg/AlertLineIcon';
import {CheckmarkIcon} from '~library/svg/v2/CheckmarkIcon';

import * as styles from './DeleteConfirmModals.module.scss';

interface DeleteConfirmModalProps {
  isOpen?: boolean;
  onDelete: (e?: Error) => void;
  onCancel: () => void;
}

interface DeleteGuestConfirmModalProps extends DeleteConfirmModalProps {
  guest: Guest;
}

export const DeleteGuestConfirmModal = memo(
  ({guest, isOpen = false, onCancel, onDelete}: DeleteGuestConfirmModalProps) => {
    const {deleteGuest, isDeleting} = useGuestApi(guest.userId);
    const isPremium = useSelector(selectIsPremium);
    const isPro = useSelector(selectIsPro);
    const isUpgrade = useSelector(selectIsUpgrade);
    const showSubtitleMessage = (isPremium || isUpgrade) && !isPro;

    const doDelete = useCallback(async () => {
      try {
        await deleteGuest();
        onDelete();
      } catch (e: unknown) {
        evite.error('Failed to delete guest', e);
        const message = getPydanticError(e, '');
        toastAlert(`Failed to delete guest.\n${message}`, {
          icon: <AlertLineIcon />,
          type: 'error',
        });
        onDelete(e as Error);
      }
    }, [deleteGuest, onDelete]);

    const body = (
      <div className={styles.modalContent}>
        <Typography variant="paragraph2" color="pebble">
          {`You are about to remove ${guest.name} from this event.`}
        </Typography>
        {showSubtitleMessage && (
          <Typography variant="paragraph2" color="pebble">
            <em>FYI: when you remove a guest, a send is credited back to your Premium package.</em>
          </Typography>
        )}
      </div>
    );

    return createPortal(
      <ResponsiveModal
        onClose={onCancel}
        title="Remove this guest?"
        body={body}
        primaryAction={doDelete}
        primaryText="Remove now"
        titleQaId="delete-guest-modal-title"
        bodyQaId="delete-guest-modal-body"
        primaryQaId="delete-guest-modal-action"
        primaryActionDisabled={isDeleting}
        secondaryText="Cancel"
        secondaryAction={onCancel}
        isOpen={isOpen}
        showSpinner={isDeleting}
      />,
      document.body
    );
  }
);

DeleteGuestConfirmModal.displayName = 'DeleteGuestConfirmModal';

interface DeleteReplyConfirmModalProps extends DeleteConfirmModalProps {
  postId: number;
}

export const DeleteReplyConfirmModal = memo(
  ({postId, isOpen = false, onCancel, onDelete}: DeleteReplyConfirmModalProps) => {
    const {deleteComment, isDeleting} = usePostsApi();

    const doDelete = useCallback(async () => {
      try {
        await deleteComment(postId);
        toastAlert('Comment deleted', {
          icon: <CheckmarkIcon />,
          type: 'success',
        });
        onDelete();
      } catch (e: unknown) {
        evite.error('Failed to delete comment', e);
        const message = getPydanticError(e, '');
        toastAlert(`Failed to delete comment.\n${message}`, {
          icon: <AlertLineIcon />,
          type: 'error',
        });
        onDelete(e as Error);
      }
    }, [deleteComment, onDelete, postId]);

    const body = (
      <div className={styles.modalContent}>
        <Typography variant="paragraph2" color="pebble">
          The comment will no longer be visible to you or anyone else.
        </Typography>
      </div>
    );

    return createPortal(
      <ResponsiveModal
        onClose={onCancel}
        title="Delete your comment?"
        body={body}
        primaryAction={doDelete}
        primaryText="Delete"
        titleQaId="delete-guest-modal-title"
        bodyQaId="delete-guest-modal-body"
        primaryQaId="delete-guest-modal-action"
        primaryActionDisabled={isDeleting}
        secondaryText="Cancel"
        secondaryAction={onCancel}
        isOpen={isOpen}
        showSpinner={isDeleting}
      />,
      document.body
    );
  }
);

DeleteReplyConfirmModal.displayName = 'DeleteReplyConfirmModal';
